import { TeamService } from 'src/app/core/service/team.service';
import { Kno2DocumentSource } from './../../core/model/kno2-document-source';
import { Kno2Service } from 'src/app/core/service/kno2.service';
import { Observable } from 'rxjs';
import { DocumentService } from 'src/app/core/service/document.service';
import { Component, Input, OnInit, ElementRef, Output, EventEmitter, ViewChild } from '@angular/core';
import { User } from 'src/app/user/model/user';
import { Message } from '../../case/model/message';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { Validators, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { MessageService } from '../../core/service/message.service';
import { UserService } from '../../core/service/user.service';
import { NGXLogger } from 'ngx-logger';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { sortBy, uniqBy, isString } from 'lodash';
import { UploadEvent } from '@progress/kendo-angular-upload';
import { CustomFile } from '../model/custom-file';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { map } from 'rxjs/operators';
import { TeamEdit } from 'src/app/team/model/team';
import { DirectorySearchComponent } from 'src/app/shared/directory-search/directory-search.component';
import { MemberService } from 'src/app/core/service/member.service';

@Component({
  standalone: false,
  selector: 'app-message-compose',
  templateUrl: './message-compose.component.html',
  styleUrls: ['./message-compose.component.scss']
})
export class MessageComposeComponent implements OnInit
{
  @ViewChild('directorySearch') public directorySearch: DirectorySearchComponent;

  @Input() public messageDialogOpened: boolean;
  @Input() public caseId: number;
  @Input() public messageList: any;
  @Input() public fromMessagesScreen: string;
  @Input() public memberId: number;
  @Input() public sendTo = '';

  @Output() public afterSave: EventEmitter<any> = new EventEmitter();
  @Output() public saving: EventEmitter<any> = new EventEmitter();

  public patientName: string;
  public patientLabel = {};
  public attachmentsEnabled = false;
  public confirmAddToCareTeamDialogOpened = false;
  public toUsersFiltered: User[] = [];
  public selectedFiles: CustomFile[] = [];
  public selectedDirectAddresses: Array<any> = [];
  public savingMessage = false;

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * Message form.
   */
  public messageForm: any;

  /**
   * Indicate if Kno2 integration is enabled
   */
  public kno2Enabled = false;

  /**
   * Indicate if community is enabled
   */
  public communityEnabled = false;

  /**
   * Kno2 document sources.
   */
  public kno2DocumentSources: Array<Kno2DocumentSource>;

  /**
   * Current user direct address loaded when kno2Enabled=True
   */
  public currentUserDirectAddress: string;

  private message: Message = new Message();
  private autoMessage = false;
  private toUsers: User[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private messageService: MessageService,
    private userService: UserService,
    private logger: NGXLogger,
    private elem: ElementRef,
    private documentService: DocumentService,
    private messageHandlerService: MessageHandlerService,
    private authenticationService: AuthenticationService,
    private messengerService: MessengerService,
    private settingService: SettingService,
    private kno2Service: Kno2Service,
    private memberService: MemberService,
    private teamService: TeamService)
  {
    this.userService.getUserById(this.authenticationService.currentUser.id, false).subscribe({
      next: (user) =>
      {
        this.currentUserDirectAddress = user.directAddress;
      }
    });
  }

  public get to(): UntypedFormControl
  {
    return this.messageForm.get('to');
  }

  public get subject(): UntypedFormControl
  {
    return this.messageForm.get('subject');
  }

  public get body(): UntypedFormControl
  {
    return this.messageForm.get('body');
  }

  public get fromDirectAddress(): UntypedFormControl
  {
    return this.messageForm.get('fromDirectAddress');
  }

  public ngOnInit(): void
  {
    this.patientName = '';
    if (this.memberId !== undefined)
    {
      this.memberService.getMember(this.memberId).subscribe({
        next: (member) =>
        {
          this.patientName = member?.name.fullName;
        }
      });
    }

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
        this.kno2Enabled = sessionSetting.kno2Enabled;
        this.communityEnabled = sessionSetting.communityEnabled;
        this.attachmentsEnabled = (sessionSetting.attachmentsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.attachmentsRoles) : false;

        const addCommunityUser = this.communityEnabled && (this.fromMessagesScreen === 'case' || this.fromMessagesScreen === 'member');
        this.userService.getCachedUsers(false, addCommunityUser, this.memberId, false, 'MessagesRoles').subscribe({
          next: (data) =>
          {
            this.toUsers = sortBy(data, ['name']);
            this.filterToUsers('');
            this.buildForm();

            if (this.kno2Enabled)
            {
              this.kno2Service.getCachedDocumentSources().subscribe({
                next: (documentSources) =>
                {
                  // Filter direct address on client since type not working on the back-end.
                  this.kno2DocumentSources = documentSources;
                }
              });

              this.messageForm.get('to').valueChanges.subscribe({
                next: (toAddresses: any) =>
                {
                  this.toValueChanges(toAddresses);
                }
              });
            }
          }
        });
      }
    });
    this.buildForm();
  }

  public filterToUsers(filterValue: any): void
  {
    this.searchTerm = filterValue;
    this.toUsersFiltered = this.toUsers
      .filter(user => user.name.toLowerCase().includes(filterValue.toLowerCase()));
  }

  /**
   * Form submission event handler.
   */
  public onSubmit(e: Event): void
  {
    this.savingMessage = true;
    this.saving.emit(true);

    const user = this.authenticationService.currentUser;
    this.message = this.messageForm.value;
    this.message.from = user.name;
    this.message.fromId = user.id;
    this.message.memberId = this.memberId;
    this.message.caseId = this.caseId;
    this.message.files = this.selectedFiles;
    this.message.to.forEach(to =>
    {
      if (user.id === to.id)
      {
        this.autoMessage = true;
      }
    });

    // If Kno2 is enabled, perform from validation. Must be done this way because control is disabled.
    // Direct address is set automatically based on the direct address from the user account.
    if (this.kno2Enabled)
    {
      // If not constains direct, remove it.
      if (!this.isAddressesContainsDirect(this.message.to))
      {
        this.message.fromDirectAddress = null;
      }
      else
      {
        if (this.fromDirectAddress.value === null)
        {
          this.messageHandlerService.displayError('FROM-DIRECT-ADDRESS-REQUIRED');
          e.preventDefault();
          return;
        }
        else
        {
          this.message.fromDirectAddress = this.fromDirectAddress.value;
        }
      }
    }

    this.messageService.saveMessage(this.message).subscribe({
      next: (data) =>
      {
        data.sentOn = new Date(data.sentOn);
        if ((this.autoMessage && this.fromMessagesScreen === 'message') || this.fromMessagesScreen !== 'message')
        {
          if (this.messageList !== undefined)
          {
            if (this.messageList.data) { this.messageList.data.unshift(data); }
            if (this.messageList.gridData.data) { this.messageList.gridData.data.unshift(data); }
          }
        }

        if (this.fromMessagesScreen === 'case')
        {
          this.messengerService.sendMessage(new MessageSubject('getCaseDetailsData'));
        }
        else if (this.fromMessagesScreen === 'member')
        {
          this.messengerService.sendMessage(new MessageSubject('reloadMessagesData'));
        }

        this.messageHandlerService.displaySuccess('SEND-SUCCESSFULLY');
        this.afterSave.emit(data);

        // After save, check if we want to add the provide to the care team
        if (this.kno2Enabled && (this.isAddressesContainsDirect(this.message.to)))
        {
          this.processSaveToCareTeam();
        }
      },
      error: (error) =>
      {
        this.logger.error(error);

        // Error occurred during save, reset the model and display error message
        this.message = new Message();
        this.selectedFiles = [];
        this.messageHandlerService.displayError(error);
        this.buildForm();

        this.messageDialogOpened = false;
        this.savingMessage = false;
        this.saving.emit(false);
      },
      complete: () =>
      {
        this.messageDialogOpened = false;
        this.savingMessage = false;
        this.saving.emit(false);
      }
    });
  }

  public save(e: Event): void
  {
    this.onSubmit(e);
  }

  public cancel(): void
  {
    this.messageDialogOpened = false;
  }

  public formValid(): boolean
  {
    return this.messageForm.valid;
  }

  /**
   * Check if user from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.enabled && !itemArgs.dataItem.inRegistration;
  }

  /**
   * File has been selected.
   *
   * @param event Event
   */
  public onFileChanged(event: UploadEvent): void
  {
    event.files.forEach((file) =>
    {
      if (this.selectedFiles.find(f => f.name === file.name) !== undefined)
      {
        this.messageHandlerService.displayError('FILE-ALREADY-ATTACHED');
        return;
      }
      this.messageForm.markAsDirty();
      const customFile = new CustomFile();
      customFile.name = file.name;
      customFile.type = file.rawFile.type;
      this.setFileFromBlob(customFile, file.rawFile);
      this.selectedFiles.push(customFile);
    });
  }

  public deleteAttachment(name: string): void
  {
    this.selectedFiles = this.selectedFiles.filter(f => f.name !== name);
  }

  /**
   * Select attachment and open the uploader.
   */
  public selectAttachment(): void
  {
    const uploadButton = this.elem.nativeElement.querySelectorAll('input[name="files"]');
    uploadButton[0].click();
  }

  /**
   * Return the proper class to used based on the file mime type.
   *
   * @param mimeType Mime Type of the file.
   * @returns String
   */
  public getFileTypeClass(mimeType: string): string
  {
    return this.documentService.getFileTypeClass(mimeType);
  }

  /**
   * Open the Search Directory dialog.
   */
  public searchDirectory(): void
  {
    this.directorySearch.execute();
  }

  /**
   * Providers or Organizations have been selected from the Kno2 dialog.
   * Set the to addresses with the direct addresses.
   *
   * @param providers List of Providers or Organizations selected from the dialog.
   */
  public onSelectProviders(providers: any): void
  {
    const selectedAddresses = this.to.value !== null ? this.to.value : [];
    // Ignored selected providers who have no direct address defined
    const filteredProviders = providers.filter(x => x.directAddress !== '' && x.directAddress !== null);
    const newAddresses = selectedAddresses.concat(filteredProviders.map(p => this.buildDirectAddress(p.directAddress)));
    this.to.setValue(uniqBy(newAddresses, 'name'));
    this.selectedDirectAddresses = selectedAddresses.concat(providers);
  }

  /**
   * Normalizer for the "to" address. This is required to handle the direct addresses within a single control.
   * The "to" address can contains valid users or direct addresses.
   */
  public toAddressNormalizer = (text$: Observable<string>): any => text$.pipe(map((text: string) =>
  {
    // Search for matching item to avoid duplicates
    const matchingValue: any = this.to.value.find((item: any) => item.name.toLowerCase() === text.toLowerCase());

    if (matchingValue)
    {
      return matchingValue; // Return the already selected matching value and the component will remove it
    }

    // Search in data
    const matchingItem: any = this.toUsersFiltered.find((item: any) => item.name.toLowerCase() === text.toLowerCase());

    if (matchingItem)
    {
      return matchingItem;
    }
    else
    {
      return this.buildDirectAddress(text);
    }
  }));

  /**
   * Check if the "TO" addresses contains a direct address. If we have a direct address we must select a from address.
   *
   * @param toAddresses List of selected "TO" addresses.
   */
  public isAddressesContainsDirect(toAddresses: any): boolean
  {
    let hasDirect = false;
    toAddresses.forEach((x: any) =>
    {
      if (isString(x.id) && x.id.startsWith('direct-'))
      {
        hasDirect = true;
      }
    });
    return hasDirect;
  }

  private processSaveToCareTeam(): void
  {
    if (this.caseId !== undefined || this.memberId !== undefined)
    {
      this.message.to.forEach(to =>
      {
        if (isString(to.id) && to.id.startsWith('direct-'))
        {
          const provider = this.selectedDirectAddresses.filter(x => x.directAddress === to.name);
          if (provider.length > 0)
          {
            this.teamService.existsProgramCategoryName(provider[0].lastName, provider[0].firstName,
              provider[0].directAddress, this.caseId, this.memberId).subscribe({
                next: (existsResponse) =>
                {
                  if (existsResponse.toString() === 'false')
                  {
                    const team = new TeamEdit();
                    team.member = this.memberId;
                    team.case = this.caseId;

                    team.firstName = provider[0].firstName !== undefined ? provider[0].firstName : '';
                    team.lastName = provider[0].lastName !== undefined ? provider[0].lastName : '';
                    team.homePhoneNumber = provider[0].telephoneNumber !== undefined ? provider[0].telephoneNumber : '';
                    team.cellPhoneNumber = provider[0].cellPhoneNumber !== undefined ? provider[0].cellPhoneNumber : '';
                    team.faxNumber = provider[0].faxNumber !== undefined ? provider[0].faxNumber : '';
                    team.organization = provider[0].organization !== undefined ? provider[0].organization : '';
                    team.email = provider[0].email !== undefined ? provider[0].email : '';
                    team.title = provider[0].title !== undefined ? provider[0].title : '';
                    team.directAddress = provider[0].directAddress !== undefined ? provider[0].directAddress : '';
                    team.npi = provider[0].npi !== undefined ? provider[0].npi : '';
                    team.providerDirectorySource = provider[0].directorySource;
                    team.streetLine = provider[0].practiceAddress ? provider[0].practiceAddress.streetLine : null;
                    team.streetLineAdditional = provider[0].practiceAddress ? provider[0].practiceAddress.streetLineAdditional : null;
                    team.city = provider[0].practiceAddress ? provider[0].practiceAddress.city : null;
                    team.postalCode = provider[0].practiceAddress ? provider[0].practiceAddress.postalCode : null;
                    team.state = provider[0].practiceAddress ? provider[0].practiceAddress.state : null;
                    team.country = provider[0].practiceAddress ? provider[0].practiceAddress.country : null;

                    this.messengerService.sendMessage(new MessageSubject('addToCareTeam', { teamEdit: team }));
                    return;
                  }
                }
              });
          }
        }
      });
    }
  }

  private toValueChanges(toAddresses: any): void
  {
    if (this.isAddressesContainsDirect(toAddresses))
    {
      this.messageForm.get('fromDirectAddress').setValidators([Validators.required]);
      this.messageForm.get('fromDirectAddress').setValue(this.currentUserDirectAddress);
    }
    else
    {
      this.messageForm.get('fromDirectAddress').setValidators([]);
    }
    this.messageForm.get('fromDirectAddress').updateValueAndValidity();
  }

  private buildForm(): void
  {
    if (this.sendTo !== '' && this.sendTo !== null && this.sendTo !== undefined)
    {
      if (this.sendTo.startsWith('direct-') && this.sendTo.substring(7))
      {
        this.message.to = [];
        this.message.to.push(this.buildDirectAddress(this.sendTo.substring(7)));
      }
      else
      {
        const user = this.toUsers.filter(u => u.username === this.sendTo);
        if (user)
        {
          this.message.to = user;
        }
      }
    }
    this.messageForm = this.formBuilder.group({
      to: [{
        value: this.message.to !== undefined && this.message.to !== null ? this.message.to : [],
        disabled: this.savingMessage
      }, Validators.required],
      subject: [{ value: this.message.subject, disabled: this.savingMessage }, Validators.required],
      body: [{ value: this.message.body, disabled: this.savingMessage }, Validators.required],
      fromDirectAddress: [{ value: this.message.fromDirectAddress, disabled: true }],
    });

    // Force value changes to make sure from direct address from is set
    this.toValueChanges(this.to.value);
  }

  /**
   * Set the selected file.
   *
   * @param file Selected file.
   */
  private setFileFromBlob(customFile: CustomFile, file: Blob): void
  {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
    {
      const base64: any = reader.result;
      customFile.data = base64.split('base64,')[1];
    }, false);
    reader.readAsDataURL(file);
  }

  /**
   * This method returned a compatible "to" address with a direct address.
   *
   * @param directAddress Current direct address to add.
   */
  private buildDirectAddress(directAddress: string): any
  {
    return { id: `direct-${directAddress}`, name: directAddress };
  }
}
